import { Boundaries } from "@planzone/core";
import { organization } from "@planzone/dto";
import { events } from "@planzone/events";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRetrieveOrganizationRequest(
  state: State,
  planzone: Boundaries["retrieveOrganization"]["request"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToPending(),
          organization: { ...organization, uuid: "" },
        },
      ];
}

function handleRetrieveOrganizationSuccess(
  state: State,
  { planzone, organization }: Boundaries["retrieveOrganization"]["success"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToSuccess(), organization },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        { planzone, request: setRequestStateToSuccess(), organization },
      ];
}

function handleRetrieveOrganizationFailure(
  state: State,
  planzone: Boundaries["retrieveOrganization"]["failure"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToFailure(),
          organization: { ...organization, uuid: "" },
        },
      ];
}

function handleEditOrganizationRequest(
  state: State,
  { planzone, nextOrganization }: Boundaries["editOrganization"]["request"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          organization: {
            ...nextOrganization,
            uuid: state[index].organization.uuid,
          },
        },
        ...state.slice(index + 1),
      ]
    : state;
}

function handleEditOrganizationSuccess(
  state: State,
  { planzone, organization }: Boundaries["editOrganization"]["success"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          organization,
        },
        ...state.slice(index + 1),
      ]
    : state;
}

function handleEditOrganizationFailure(
  state: State,
  { planzone, organization }: Boundaries["editOrganization"]["failure"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], organization },
        ...state.slice(index + 1),
      ]
    : state;
}

const reduceState = createStateReducer(initialState, {
  [events.retrieveOrganizationRequested]: handleRetrieveOrganizationRequest,
  [events.retrieveOrganizationSucceeded]: handleRetrieveOrganizationSuccess,
  [events.retrieveOrganizationFailed]: handleRetrieveOrganizationFailure,
  [events.editOrganizationRequested]: handleEditOrganizationRequest,
  [events.editOrganizationSucceeded]: handleEditOrganizationSuccess,
  [events.editOrganizationFailed]: handleEditOrganizationFailure,
});

export type { State, Selector };

export { reduceState, initialState, createSelector };
